import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, ConfirmationDialog } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc, trpcClient, trpcPostUnbatched } from '@/api/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FaInfoCircle } from 'react-icons/fa';
import { formatTimeHorizon, sortHorizons } from '@/utils/timeHorizonGenerator';
import { showToast } from '@/components/Layout/Toast';
import type { Row } from '@/components/UI/dataTableConfig';
import { useAuth } from '@/context/AuthContext';
import { useParameterPreferences } from '@/hooks/useParameterPreferences';
import { useScorecardRuleStatus } from '@/hooks/useScorecardRuleStatus';
import ParameterAnalysisDetailsPanel from './parameterDiscovery/ParameterAnalysisDetailsPanel';
import CustomBucketsModal from './parameterDiscovery/CustomBucketsModal';
import AddRuleModal from './parameterDiscovery/AddRuleModal';
import JobStatusOverview from './parameterDiscovery/JobStatusOverview';
import { useParameterTableColumns } from './parameterDiscovery/useParameterTableColumns';
import { useScorecardQuickAdd } from './parameterDiscovery/useScorecardQuickAdd';
import ParameterDetailsContent from './parameterDiscovery/ParameterDetailsContent';
import AnalysisScopeCard from './parameterDiscovery/AnalysisScopeCard';
import ObjectiveModeControl, {
    type ObjectiveMode,
    type RealizedOutcomeMaterializationEntityType
} from './parameterDiscovery/ObjectiveModeControl';
import ScorecardTargetCard from './parameterDiscovery/ScorecardTargetCard';
import FeatureInteractionFindingsCard from './parameterDiscovery/FeatureInteractionFindingsCard';
import PendingAnalysisCard from './parameterDiscovery/PendingAnalysisCard';
import AnalysisSummaryStats from './parameterDiscovery/AnalysisSummaryStats';
import ParameterAnalysisTableCard from './parameterDiscovery/ParameterAnalysisTableCard';
import {
    buildBucketParameterIdentity,
    buildParameterTableRow,
    getBucketIdentityKey,
    getParameterDisplayLabelWithWindow,
    getParameterTrailingWindow,
    groupParameterDiscoveryJobs,
    readPersistedScorecardId
} from './parameterDiscovery/helpers';
import type {
    BucketAlgorithm,
    BucketMode,
    BucketParameterIdentity,
    CanonicalParameterFeatureIdentity,
    ParameterBucketBoundary,
    ParameterColumnPreset,
    ParameterDiscoveryMetric,
    ParameterGroup,
    ParameterTableRow,
    ParameterTrailingWindowFilter,
    PendingAnalysisResult,
    ReadyAnalysisResult,
    ScorecardRuleCandidate,
    ScorecardStrategyContext
} from './parameterDiscovery/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export type { OptimizationOpportunity } from './parameterDiscovery/types';

interface ParameterDiscoveryProps {
    experimentId: number;
    executionStatus: 'draft' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'deleting';
    discoveryStatus: 'disabled' | 'not_started' | 'not_applicable' | 'preparing_objective' | 'pending' | 'running' | 'completed' | 'failed';
    initialDuration?: string;
    initialHorizonSeconds?: number;
    initialMetric?: ParameterDiscoveryMetric;
    autoOpenAnalysis?: boolean;
    onScopeChange?: (scope: { duration: string; horizonSeconds: number; metric: ParameterDiscoveryMetric }) => void;
    strategyId?: number | null;
    materializationEntityType?: RealizedOutcomeMaterializationEntityType | null;
    durations?: {
        duration: string;
        horizons: number[];
        totalEntries: number;
        totalEntities: number;
        metrics: Record<string, HorizonMetrics>;
    }[];
}

interface HorizonMetrics {
    avgProfitPercent: number | null;
    avgSpyProfitPercent: number | null;
    avgAlphaPercent: number | null;
    winRate: number | null;
    winRateVsSpy: number | null;
    avgMaxProfitPercent: number | null;
    avgMaxLossPercent: number | null;
    profitableEntries: number;
    totalEntries: number;
    optionAvgProfitPercent?: number | null;
    optionWinRate?: number | null;
    optionAvgMaxProfitPercent?: number | null;
    optionAvgMaxLossPercent?: number | null;
    optionProfitableEntries?: number;
    optionTotalEntries?: number;
    optionOutperformanceRate?: number | null;
    optionAvgLeverage?: number | null;
    optionSuccessRate?: number | null;
}

interface AnalysisScopeKeyParams {
    duration: string;
    horizonSeconds: number;
    metric: ParameterDiscoveryMetric;
    objectiveMode: ObjectiveMode;
    realizedOutcomeSetId: number | null;
}

const ParameterDiscovery: React.FC<ParameterDiscoveryProps> = ({
    experimentId,
    executionStatus,
    discoveryStatus,
    durations = [],
    initialDuration,
    initialHorizonSeconds,
    initialMetric = 'stock',
    autoOpenAnalysis = false,
    onScopeChange,
    strategyId = null,
    materializationEntityType = null
}) => {
    const { user } = useAuth();
    const userId = user?.id;
    const queryClient = useQueryClient();
    // Initialize with first available duration and horizon from experiment data
    const firstDuration = initialDuration || durations[0]?.duration || '1m';
    const firstHorizon = initialHorizonSeconds || durations[0]?.horizons?.[0] || 300;

    const [selectedDurationPreference, setSelectedDuration] = useState<string>(firstDuration);
    const [selectedHorizonPreference, setSelectedHorizon] = useState<number>(firstHorizon);
    const [hasInitializedPreference, setHasInitialized] = useState(false);
    const [retryState, setRetryState] = useState<{ key: string; count: number } | null>(null);
    const [queuedAnalysisKey, setQueuedAnalysisKey] = useState<string | null>(null);
    const [optimizationMode, setOptimizationMode] = useState<ParameterDiscoveryMetric>(initialMetric);
    const [objectiveMode, setObjectiveMode] = useState<ObjectiveMode>('fixed_horizon');
    const [realizedOutcomeSetId, setRealizedOutcomeSetId] = useState<number | null>(null);
    const [minEntriesThreshold, setMinEntriesThreshold] = useState<number>(30);
    const [parameterColumnPreset, setParameterColumnPreset] = useState<ParameterColumnPreset>('core');
    const [trailingWindowFilter, setTrailingWindowFilter] = useState<ParameterTrailingWindowFilter>('all');
    const [bucketModalOpen, setBucketModalOpen] = useState(false);
    const [bucketModalIdentity, setBucketModalIdentity] = useState<BucketParameterIdentity | null>(null);
    const [bucketAlgorithm, setBucketAlgorithm] = useState<BucketAlgorithm>('equalWidth');
    const [bucketCount, setBucketCount] = useState<number>(10);
    const [bucketMode, setBucketMode] = useState<BucketMode>('auto');
    const [customRanges, setCustomRanges] = useState<ParameterBucketBoundary[]>([]);
    const [customBucketsByIdentityKey, setCustomBucketsByIdentityKey] = useState<Record<string, ParameterGroup[]>>({});
    const [parameterDetailsByKey, setParameterDetailsByKey] = useState<Record<string, ParameterTableRow>>({});

    // Initialize parameter preferences hook (global across all experiments)
    const {
        showArchived,
        setShowArchived,
        viewMode,
        setViewMode,
        isUpdating,
        getStatus,
        togglePin,
        toggleArchive,
        resetAll,
        shouldShowParameter,
        sortByPreference,
        pinnedCount,
        archivedCount
    } = useParameterPreferences({
        enabled: executionStatus === 'completed'
    });

    // Fetch experiment details (strategy scope for scorecards)
    const experimentDetailsQuery = useQuery({
        ...trpc.experiment.status.queryOptions({ experimentId }),
        enabled: executionStatus === 'completed'
    });

    // Fetch available analyses for this experiment
    const availableAnalysesQuery = useQuery({
        ...trpc.experiment.getAvailableAnalyses.queryOptions({ experimentId }),
        enabled: executionStatus === 'completed',
        staleTime: 60000 // Cache for 1 minute
    });

    // Fetch parameter discovery jobs for this experiment
    const jobsQuery = useQuery({
        ...trpc.experiment.getParameterDiscoveryJobs.queryOptions({ experimentId }),
        enabled: executionStatus === 'completed',
        refetchInterval: 5000, // Poll every 5 seconds
        staleTime: 5000
    });

    // Extract unique durations from experiment data
    const availableDurations = useMemo(() => {
        const durationsSet = new Set<string>();

        // Add durations from experiment results
        durations.forEach((d) => {
            durationsSet.add(d.duration);
        });

        return Array.from(durationsSet).sort();
    }, [durations]);

    const selectedDuration = useMemo(() => {
        if (availableDurations.length === 0) return selectedDurationPreference;
        return availableDurations.includes(selectedDurationPreference) ? selectedDurationPreference : availableDurations[0];
    }, [availableDurations, selectedDurationPreference]);

    const openBucketModal = useCallback((parameterRow: ParameterTableRow) => {
        setBucketModalIdentity(buildBucketParameterIdentity(parameterRow));
        // Reset algorithm and bucket count for each new parameter to keep UX predictable
        setBucketAlgorithm('equalWidth');
        setBucketCount(10);
        setBucketMode('auto');
        setCustomRanges([]);
        setBucketModalOpen(true);
    }, []);

    const closeBucketModal = useCallback(() => {
        setBucketModalOpen(false);
    }, []);

    // Get horizons specific to selected duration
    const availableHorizons = useMemo(() => {
        const horizonsSet = new Set<number>();

        // Find horizons for selected duration in experiment data
        const selectedDurationData = durations.find((d) => d.duration === selectedDuration);

        if (selectedDurationData) {
            selectedDurationData.horizons.forEach((h) => horizonsSet.add(h));
        }

        // Also include any cached analyses for this duration
        if (availableAnalysesQuery.data) {
            availableAnalysesQuery.data
                .filter((analysis: { duration: string }) => analysis.duration === selectedDuration)
                .forEach((analysis: { horizonSeconds: number }) => {
                    horizonsSet.add(analysis.horizonSeconds);
                });
        }

        // Use sortHorizons so offset horizons come first, then event horizons (EOD, Next Open)
        return sortHorizons(Array.from(horizonsSet));
    }, [selectedDuration, durations, availableAnalysesQuery.data]);

    const selectedHorizon = useMemo(() => {
        if (availableHorizons.length === 0) return selectedHorizonPreference;
        return availableHorizons.includes(selectedHorizonPreference) ? selectedHorizonPreference : availableHorizons[0];
    }, [availableHorizons, selectedHorizonPreference]);

    const hasInitialized = hasInitializedPreference || autoOpenAnalysis;
    const realizedOutcomeSetMissing = objectiveMode === 'strategy_exit' && realizedOutcomeSetId === null;

    useEffect(() => {
        if (!hasInitialized) return;
        onScopeChange?.({ duration: selectedDuration, horizonSeconds: selectedHorizon, metric: optimizationMode });
    }, [hasInitialized, onScopeChange, optimizationMode, selectedDuration, selectedHorizon]);

    const detailCacheScopeKey = useMemo(
        () => `${experimentId}:${selectedDuration}:${selectedHorizon}`,
        [experimentId, selectedDuration, selectedHorizon]
    );

    // Process and group jobs by status
    const groupedJobs = useMemo(
        () => groupParameterDiscoveryJobs(jobsQuery.data?.jobs || [], availableAnalysesQuery.data || []),
        [jobsQuery.data, availableAnalysesQuery.data]
    );

    // Fetch parameter analysis for selected duration and horizon - only when initialized
    const analysisQuery = useQuery({
        ...trpcPostUnbatched.experiment.getParameterAnalysisSummary.queryOptions({
            experimentId,
            duration: selectedDuration,
            horizonSeconds: selectedHorizon,
            metric: optimizationMode,
            objectiveMode,
            realizedOutcomeSetId
        }),
        enabled: hasInitialized && executionStatus === 'completed' && !!selectedDuration && !!selectedHorizon && !realizedOutcomeSetMissing,
        staleTime: 300000 // Cache for 5 minutes
    });

    const analysisResult = analysisQuery.data;

    const readyAnalysis: ReadyAnalysisResult | null = analysisResult && 'parameters' in analysisResult ? analysisResult : null;

    const pendingAnalysis: PendingAnalysisResult | null = analysisResult && 'pipelineStatus' in analysisResult ? analysisResult : null;
    const dataQuality = readyAnalysis?.dataQuality ?? pendingAnalysis?.pipelineStatus?.dataQuality;

    const getParameterDisplayLabel = useCallback(
        (parameter: { path: string; canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined }): string => {
            return getParameterDisplayLabelWithWindow(parameter);
        },
        []
    );

    // Handle analysis query errors
    useEffect(() => {
        if (analysisQuery.error) {
            const { error } = analysisQuery;

            // Check for Zod validation errors
            if (error?.data?.zodError) {
                console.error('Parameter analysis validation error:', error.data.zodError);
                showToast('Data validation error - please refresh the page and try again', 'error', true);
            } else if (error?.message) {
                console.error('Parameter analysis error:', error);
                showToast(error.message || 'Failed to load parameter analysis', 'error');
            } else {
                console.error('Unknown parameter analysis error:', error);
                showToast('Failed to load parameter analysis', 'error');
            }
        }
    }, [analysisQuery.error]);

    const buildAnalysisKey = useCallback(
        (params: AnalysisScopeKeyParams) =>
            `${experimentId}:${params.duration}:${params.horizonSeconds}:${params.metric}:${params.objectiveMode}:${
                params.realizedOutcomeSetId ?? 'none'
            }`,
        [experimentId]
    );

    const retryKey = buildAnalysisKey({
        duration: selectedDuration,
        horizonSeconds: selectedHorizon,
        metric: optimizationMode,
        objectiveMode,
        realizedOutcomeSetId
    });

    const retryCount = pendingAnalysis && retryState?.key === retryKey ? retryState.count : 0;

    const incrementRetryCount = useCallback(() => {
        setRetryState((prev) => ({
            key: retryKey,
            count: prev?.key === retryKey ? prev.count + 1 : 1
        }));
    }, [retryKey]);

    const pipelineStatus = pendingAnalysis?.pipelineStatus?.overallStatus;
    const isPipelineActive = pipelineStatus === 'pending' || pipelineStatus === 'running';
    const isExplicitRepairQueued = queuedAnalysisKey === retryKey && !readyAnalysis;

    // Auto-refresh only when work is actually active or after the user explicitly queues repair work.
    useEffect(() => {
        if (pendingAnalysis && hasInitialized && (isPipelineActive || isExplicitRepairQueued)) {
            // Set up auto-refresh every 5 seconds until processing completes
            const timer = setTimeout(() => {
                void analysisQuery.refetch();
                incrementRetryCount();
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [analysisQuery.refetch, hasInitialized, incrementRetryCount, isExplicitRepairQueued, isPipelineActive, pendingAnalysis]);

    // Queue parameter discovery job mutation
    const queueJobMutation = useMutation({
        mutationFn: async (params: {
            experimentId: number;
            duration: string;
            horizonSeconds: number;
            metric: ParameterDiscoveryMetric;
            objectiveMode: ObjectiveMode;
            realizedOutcomeSetId: number | null;
            priority?: 'high' | 'normal' | 'low';
        }) => {
            return trpcClient.experiment.queueParameterDiscovery.mutate({
                experimentId: params.experimentId,
                duration: params.duration,
                horizonSeconds: params.horizonSeconds,
                priority: params.priority || 'high',
                ...(params.objectiveMode === 'strategy_exit'
                    ? {
                          objectiveMode: params.objectiveMode,
                          ...(params.realizedOutcomeSetId ? { realizedOutcomeSetId: params.realizedOutcomeSetId } : {})
                      }
                    : { objectiveMode: params.objectiveMode })
            });
        },
        onSuccess: (result, variables) => {
            if (result.success) {
                showToast(
                    result.message ||
                        `Parameter discovery job queued for ${variables.duration} at ${formatTimeHorizon(variables.horizonSeconds)}`,
                    'success'
                );

                setQueuedAnalysisKey(
                    buildAnalysisKey({
                        duration: variables.duration,
                        horizonSeconds: variables.horizonSeconds,
                        metric: variables.metric,
                        objectiveMode: variables.objectiveMode,
                        realizedOutcomeSetId: variables.realizedOutcomeSetId
                    })
                );
            } else {
                showToast(result.message || 'Parameter discovery could not be queued for this duration/horizon', 'warning');
            }

            // Refetch jobs to update status
            void jobsQuery.refetch();
            void analysisQuery.refetch();
        },
        onError: (error: unknown) => {
            const typedError = error as { message?: string };
            showToast(typedError.message || 'Failed to queue parameter discovery job', 'error');
        }
    });

    const handleRefresh = () => {
        queueJobMutation.mutate({
            experimentId,
            duration: selectedDuration,
            horizonSeconds: selectedHorizon,
            metric: optimizationMode,
            objectiveMode,
            realizedOutcomeSetId,
            priority: 'high'
        });
    };

    const handleInitialAnalysis = () => {
        // Respect the user's current selection. If an exact cached analysis exists for
        // (selectedDuration, selectedHorizon), show it. Otherwise, queue a job for that pair.
        const cached = availableAnalysesQuery.data || [];

        const hasExactCached = cached.some(
            (a: { duration: string; horizonSeconds: number }) => a.duration === selectedDuration && a.horizonSeconds === selectedHorizon
        );

        if (hasExactCached) {
            // Enable the query; selection remains unchanged so the cached summary fetches this pair.
            setHasInitialized(true);
        } else {
            // Queue analysis for the selected pair and enable the query; selection remains unchanged.
            queueJobMutation.mutate({
                experimentId,
                duration: selectedDuration,
                horizonSeconds: selectedHorizon,
                metric: optimizationMode,
                objectiveMode,
                realizedOutcomeSetId,
                priority: 'normal'
            });

            setHasInitialized(true);
        }
    };

    // Handler for viewing a completed analysis
    const handleViewAnalysis = (duration: string, horizonSeconds: number) => {
        setSelectedDuration(duration);
        setSelectedHorizon(horizonSeconds);
        setHasInitialized(true);

        // Scroll to results section after a brief delay
        setTimeout(() => {
            const resultsElement = document.getElementById('parameter-results');

            if (resultsElement) {
                resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    // Handler for retrying a failed job
    const handleRetryJob = (duration: string, horizonSeconds: number) => {
        queueJobMutation.mutate({
            experimentId,
            duration,
            horizonSeconds,
            metric: optimizationMode,
            objectiveMode,
            realizedOutcomeSetId,
            priority: 'high'
        });
    };

    // Get job status for current selection
    // const currentJobStatus = useMemo(() => {
    //     if (!jobsQuery.data?.jobs) return null;
    //     return jobsQuery.data.jobs.find(
    //         (job: { duration: string; horizonSeconds: number }) =>
    //             job.duration === selectedDuration && job.horizonSeconds === selectedHorizon
    //     );
    // }, [jobsQuery.data, selectedDuration, selectedHorizon]);

    // Get job status badge
    // const getJobStatusBadge = (duration: string, horizon: number) => {
    //     const job = jobsQuery.data?.jobs?.find(
    //         (j: { duration: string; horizonSeconds: number }) =>
    //             j.duration === duration && j.horizonSeconds === horizon
    //     );
    //
    //     if (!job) return null;
    //
    //     switch (job.status) {
    //         case 'completed':
    //             return (
    //                 <Badge className="ml-2" variant="success">
    //                     <FaCheckCircle className="mr-1 inline" size={10} />
    //                     Complete
    //                 </Badge>
    //             );
    //         case 'running':
    //             return (
    //                 <Badge className="ml-2" variant="warning">
    //                     <FaSpinner className="mr-1 inline animate-spin" size={10} />
    //                     Running
    //                 </Badge>
    //             );
    //         case 'pending':
    //             return (
    //                 <Badge className="ml-2" variant="secondary">
    //                     <FaClock className="mr-1 inline" size={10} />
    //                     Queued
    //                 </Badge>
    //             );
    //         case 'failed':
    //             return (
    //                 <Badge className="ml-2" variant="danger">
    //                     <FaExclamationTriangle className="mr-1 inline" size={10} />
    //                     Failed
    //                 </Badge>
    //             );
    //         default:
    //             return null;
    //     }
    // };

    // Transform parameters data for table
    const tableData = useMemo((): ParameterTableRow[] => {
        if (!readyAnalysis?.parameters) return [];

        const rows = Object.entries(readyAnalysis.parameters).map(([path, analysis]) => buildParameterTableRow(path, analysis));

        // Sort by impact score by default (descending) - using appropriate mode
        return rows.sort((a, b) => {
            const aScore = optimizationMode === 'options' ? (a.optionImpactScore ?? 0) : (a.impactScore ?? 0);

            const bScore = optimizationMode === 'options' ? (b.optionImpactScore ?? 0) : (b.impactScore ?? 0);

            return bScore - aScore;
        });
    }, [readyAnalysis, optimizationMode]);

    const bucketModalParameter = useMemo(() => {
        if (!bucketModalIdentity) {
            return null;
        }

        return (
            tableData.find((row) => {
                const bucketKey = getBucketIdentityKey(row);

                return (
                    bucketKey === bucketModalIdentity.bucketKey ||
                    row.parameterKey === bucketModalIdentity.parameterKey ||
                    row.path === bucketModalIdentity.parameterPath
                );
            }) ?? null
        );
    }, [bucketModalIdentity, tableData]);

    const trailingWindowCounts = useMemo((): Record<ParameterTrailingWindowFilter, number> => {
        const trailing = tableData.filter((row) => getParameterTrailingWindow(row) !== null).length;

        return {
            all: tableData.length,
            base: tableData.length - trailing,
            trailing
        };
    }, [tableData]);

    const windowFilteredTableData = useMemo(() => {
        if (trailingWindowFilter === 'all') {
            return tableData;
        }

        return tableData.filter((row) => {
            const hasTrailingWindow = getParameterTrailingWindow(row) !== null;
            return trailingWindowFilter === 'trailing' ? hasTrailingWindow : !hasTrailingWindow;
        });
    }, [tableData, trailingWindowFilter]);

    // Apply preference-based filtering and sorting to tableData
    const filteredTableData = useMemo(() => {
        // Filter based on preferences
        const filtered = windowFilteredTableData.filter((row) => shouldShowParameter(row.preferenceIdentity.preferenceKey));

        // Sort with pinned items first, then by impact score
        return filtered.sort((a, b) => {
            // First sort by preference status
            const preferenceSort = sortByPreference(a.preferenceIdentity, b.preferenceIdentity);
            if (preferenceSort !== 0) return preferenceSort;

            // Then sort by impact score (already sorted in tableData)
            const aScore = optimizationMode === 'options' ? (a.optionImpactScore ?? 0) : (a.impactScore ?? 0);
            const bScore = optimizationMode === 'options' ? (b.optionImpactScore ?? 0) : (b.impactScore ?? 0);
            return bScore - aScore;
        });
    }, [windowFilteredTableData, shouldShowParameter, sortByPreference, optimizationMode]);

    const handleParameterDetailsLoaded = useCallback(
        (row: ParameterTableRow) => {
            setParameterDetailsByKey((prev) => ({
                ...prev,
                [`${detailCacheScopeKey}:${getBucketIdentityKey(row)}`]: row,
                [`${detailCacheScopeKey}:${row.path}`]: row,
                [`${detailCacheScopeKey}:${row.parameterKey}`]: row
            }));
        },
        [detailCacheScopeKey]
    );

    // Handler for scoped reset - only resets preferences for visible parameters
    const handleScopedReset = useCallback(() => {
        const visibleIdentities = filteredTableData.map((row) => row.preferenceIdentity);
        void resetAll(visibleIdentities);
    }, [filteredTableData, resetAll]);

    const strategyContext: ScorecardStrategyContext | null = React.useMemo(() => {
        const sid = experimentDetailsQuery.data?.experiment?.strategyId;
        return sid ? { strategyId: sid } : null;
    }, [experimentDetailsQuery.data?.experiment?.strategyId]);

    const scorecardsQuery = useQuery({
        ...trpc.scorecards.list.queryOptions(),
        enabled: !!strategyContext
    });

    // Only editable scorecards with compatible training context may receive discovered rules.
    // Baseline readiness no longer implies immutability.
    const filteredScorecards = useMemo(() => {
        if (!scorecardsQuery.data) return [];
        return scorecardsQuery.data.filter(
            (s) =>
                s.experimentId === experimentId &&
                s.duration === selectedDuration &&
                s.horizonSeconds === selectedHorizon &&
                s.lockedAt === null &&
                s.initializationStatus !== 'extracting'
        );
    }, [scorecardsQuery.data, experimentId, selectedDuration, selectedHorizon]);

    // Page-level selected scorecard (persisted by user+scope)
    const [selectedScorecardIdTopPreference, setSelectedScorecardIdTop] = useState<number | null>(null);

    const lastUsedKey = useMemo(() => {
        const sid = strategyContext?.strategyId ?? null;
        if (!userId || !sid) return null;
        return `p314:lastScorecard:strategy:${sid}:user:${userId}`;
    }, [strategyContext, userId]);

    const persistedScorecardIdTop = readPersistedScorecardId(lastUsedKey);

    const selectedScorecardIdTop = useMemo(() => {
        if (!filteredScorecards.length) return null;

        const isAvailable = (id: number | null): id is number => id !== null && filteredScorecards.some((s) => s.id === id);

        if (isAvailable(selectedScorecardIdTopPreference)) return selectedScorecardIdTopPreference;
        if (isAvailable(persistedScorecardIdTop)) return persistedScorecardIdTop;

        return filteredScorecards[0]?.id ?? null;
    }, [filteredScorecards, persistedScorecardIdTop, selectedScorecardIdTopPreference]);

    useEffect(() => {
        if (!lastUsedKey) return;

        if (!filteredScorecards.length) {
            localStorage.removeItem(lastUsedKey);
            return;
        }

        const stored = localStorage.getItem(lastUsedKey);
        const parsed = stored ? Number(stored) : NaN;

        if (!Number.isFinite(parsed) || !filteredScorecards.some((s) => s.id === parsed)) {
            localStorage.removeItem(lastUsedKey);
        }
    }, [filteredScorecards, lastUsedKey]);

    // Initialize scorecard rule status hook (for showing which values are in scorecard)
    const { isCanonicalValueInScorecard, getCanonicalFeatureValueCount } = useScorecardRuleStatus({
        scorecardId: selectedScorecardIdTop,
        enabled: executionStatus === 'completed' && !!selectedScorecardIdTop
    });

    // Define columns for main table
    const visibleParameterColumns = useParameterTableColumns({
        getCanonicalFeatureValueCount,
        getParameterDisplayLabel,
        getStatus,
        isCanonicalValueInScorecard,
        isUpdating,
        openBucketModal,
        optimizationMode,
        parameterColumnPreset,
        selectedScorecardIdTop,
        toggleArchive,
        togglePin
    });

    // Modal state: add to scorecard
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedScorecardId, setSelectedScorecardId] = useState<number | null>(null);
    const [createNew, setCreateNew] = useState<boolean>(false);

    // Candidate group (built from table row)
    const [candidate, setCandidate] = useState<ScorecardRuleCandidate | null>(null);

    const persistSelectedTop = (id: number | null) => {
        // Validate the ID exists in filtered scorecards before persisting
        if (id !== null && !filteredScorecards.some((s) => s.id === id)) {
            console.warn('Attempted to select invalid scorecard ID:', id);
            showToast('Invalid scorecard selection', 'error');
            return;
        }

        setSelectedScorecardIdTop(id);
        if (!lastUsedKey) return;
        if (id) localStorage.setItem(lastUsedKey, String(id));
        else localStorage.removeItem(lastUsedKey);
    };

    // Query invalidation callback for scorecards
    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: trpc.scorecards.list.queryOptions().queryKey
        });
    }, [queryClient]);

    const handleRuleAdded = () => {
        // Invalidate scorecards query to refresh the list
        invalidateList();
        void scorecardsQuery.refetch();
    };

    const { bulkAddingPath, bulkConfirm, setBulkConfirm, handleQuickAddAllClick, quickAddRule, quickAddAllForParameter } =
        useScorecardQuickAdd({
            customBucketsByIdentityKey,
            detailCacheScopeKey,
            filteredScorecards,
            invalidateList,
            isCanonicalValueInScorecard,
            lastUsedKey,
            minEntriesThreshold,
            optimizationMode,
            parameterDetailsByKey,
            refetchScorecards: scorecardsQuery.refetch,
            selectedScorecardIdTop,
            setSelectedScorecardIdTop,
            tableData
        });

    // Preference: show advanced metrics in value tables (default: unchecked)
    const [showAdvancedValueMetrics, setShowAdvancedValueMetrics] = useState<boolean>(() => {
        try {
            return localStorage.getItem('p314:paramDisc:showAdvanced') === '1';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('p314:paramDisc:showAdvanced', showAdvancedValueMetrics ? '1' : '0');
        } catch {
            console.error('Error persisting advanced value metrics', showAdvancedValueMetrics);
        }
    }, [showAdvancedValueMetrics]);

    const handleOpenAddModal = (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => {
        setCandidate({
            parameterPath: parameterRow.path,
            canonicalFeature: parameterRow.canonicalFeature,
            scorecardCandidate: parameterRow.scorecardCandidate,
            label,
            baseline: parameterRow.baseline,
            group
        });

        setSelectedScorecardId(selectedScorecardIdTop ?? null);
        setCreateNew(false);
        setAddModalOpen(true);
    };

    const handleCloseModal = () => {
        setAddModalOpen(false);
        setCandidate(null);
        setSelectedScorecardId(null);
        setCreateNew(false);
    };

    const renderParameterDetails = (detailRow: ParameterTableRow) => (
        <ParameterDetailsContent
            bulkAddingPath={bulkAddingPath}
            customBucketsByIdentityKey={customBucketsByIdentityKey}
            getParameterDisplayLabel={getParameterDisplayLabel}
            isCanonicalValueInScorecard={isCanonicalValueInScorecard}
            minEntriesThreshold={minEntriesThreshold}
            optimizationMode={optimizationMode}
            parameterRow={detailRow}
            selectedScorecardIdTop={selectedScorecardIdTop}
            setShowAdvancedValueMetrics={setShowAdvancedValueMetrics}
            showAdvancedValueMetrics={showAdvancedValueMetrics}
            onOpenAddModal={handleOpenAddModal}
            onQuickAddAllClick={handleQuickAddAllClick}
            onQuickAddRule={quickAddRule}
        />
    );

    const renderSubComponent = ({ row }: { row: Row<ParameterTableRow> }) => (
        <ParameterAnalysisDetailsPanel
            duration={selectedDuration}
            experimentId={experimentId}
            horizonSeconds={selectedHorizon}
            metric={optimizationMode}
            objectiveMode={objectiveMode}
            parameterRow={row.original}
            realizedOutcomeSetId={realizedOutcomeSetId}
            renderContent={renderParameterDetails}
            onDetailsLoaded={handleParameterDetailsLoaded}
        />
    );

    if (executionStatus !== 'completed') {
        return (
            <Card className="bg-zinc-900 p-6">
                <div className="text-center text-zinc-400">
                    <FaInfoCircle className="text-4xl mx-auto mb-4" />
                    <p>Parameter discovery is only available for completed experiments.</p>
                    <p className="text-sm mt-2">Execution status: {executionStatus}</p>
                    <p className="text-sm text-zinc-500 mt-1">Discovery status: {discoveryStatus}</p>
                </div>
            </Card>
        );
    }

    if (availableAnalysesQuery.isLoading) {
        return <DataLoadingSpinner text="Loading available analyses..." />;
    }

    if (availableAnalysesQuery.error) {
        return (
            <Card className="bg-zinc-900 p-6">
                <div className="text-red-400">Error loading analyses: {availableAnalysesQuery.error.message}</div>
            </Card>
        );
    }

    // Check if we have experiment data to analyze
    if (durations.length === 0) {
        return (
            <Card className="bg-zinc-900 p-6">
                <div className="text-center text-zinc-400">
                    <FaInfoCircle className="text-4xl mx-auto mb-4" />
                    <p>No experiment data available for analysis.</p>
                    <p className="text-sm mt-2">The experiment may not have generated any entries.</p>
                </div>
            </Card>
        );
    }

    // Show loading state when analysis is running
    if (queueJobMutation.isPending) {
        return (
            <Card className="bg-zinc-900 p-6">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
                    <p className="text-zinc-400 mb-2">Analyzing parameters...</p>
                    <p className="text-zinc-500 text-sm">
                        Processing {selectedDuration} duration with {formatTimeHorizon(selectedHorizon)} horizon
                    </p>
                    <div className="mt-4 text-zinc-600 text-xs">This may take a few moments depending on the experiment size</div>
                </div>
            </Card>
        );
    }

    // If not initialized yet, show job status overview
    if (!hasInitialized) {
        return (
            <JobStatusOverview
                availableDurations={availableDurations}
                availableHorizons={availableHorizons}
                experimentId={experimentId}
                groupedJobs={groupedJobs}
                isQueueJobPending={queueJobMutation.isPending}
                materializationEntityType={materializationEntityType}
                objectiveMode={objectiveMode}
                realizedOutcomeSetId={realizedOutcomeSetId}
                selectedDuration={selectedDuration}
                selectedHorizon={selectedHorizon}
                setSelectedDuration={setSelectedDuration}
                setSelectedHorizon={setSelectedHorizon}
                strategyId={strategyId}
                onObjectiveModeChange={setObjectiveMode}
                onRealizedOutcomeSetIdChange={setRealizedOutcomeSetId}
                onRetryJob={handleRetryJob}
                onStartAnalysis={handleInitialAnalysis}
                onViewAnalysis={handleViewAnalysis}
            />
        );
    }

    return (
        <div className="space-y-6" id="parameter-results">
            <ObjectiveModeControl
                duration={selectedDuration}
                experimentId={experimentId}
                horizonSeconds={selectedHorizon}
                materializationEntityType={materializationEntityType}
                objectiveMode={objectiveMode}
                realizedOutcomeSetId={realizedOutcomeSetId}
                selectId="parameter-discovery-realized-outcome-set"
                strategyId={strategyId}
                onObjectiveModeChange={setObjectiveMode}
                onRealizedOutcomeSetIdChange={setRealizedOutcomeSetId}
            />

            {dataQuality && (dataQuality.rankingStatus !== 'complete' || dataQuality.entryExcludedSessionCount > 0) && (
                <div className="rounded border border-amber-800 bg-amber-950/30 p-3 text-sm text-amber-200">
                    Results are{' '}
                    {dataQuality.rankingStatus === 'conditional'
                        ? 'conditional on the priced subset'
                        : dataQuality.rankingStatus === 'unverified'
                          ? 'unverified for outcome coverage'
                          : dataQuality.rankingStatus === 'complete'
                            ? 'complete for the retained cohort'
                            : 'based on insufficient coverage'}
                    . {dataQuality.pricedEntries.toLocaleString()} of {dataQuality.totalEntries.toLocaleString()} entries have verified
                    prices; {dataQuality.unresolvedEntries.toLocaleString()} unresolved, {dataQuality.immatureEntries.toLocaleString()} not
                    mature.
                    {dataQuality.entryExcludedSessionCount > 0 && (
                        <>
                            {' '}
                            {dataQuality.entryExcludedSessionCount} input sessions across {dataQuality.entryExcludedAssetCount} assets were
                            excluded from this cohort.
                        </>
                    )}
                    {dataQuality.lifecycleUnavailableEntries > 0 && (
                        <> {dataQuality.lifecycleUnavailableEntries} have recorded lifecycle events without a terminal value.</>
                    )}
                    {dataQuality.rankingStatus !== 'complete' && <> These rankings do not certify a complete-cohort winner.</>}
                </div>
            )}

            <AnalysisScopeCard
                analysisResult={analysisResult}
                availableDurations={availableDurations}
                availableHorizons={availableHorizons}
                cachedAnalyses={availableAnalysesQuery.data}
                isQueueJobPending={queueJobMutation.isPending}
                jobs={jobsQuery.data?.jobs}
                minEntriesThreshold={minEntriesThreshold}
                optimizationMode={optimizationMode}
                selectedDuration={selectedDuration}
                selectedHorizon={selectedHorizon}
                setMinEntriesThreshold={setMinEntriesThreshold}
                setOptimizationMode={setOptimizationMode}
                setSelectedDuration={setSelectedDuration}
                setSelectedHorizon={setSelectedHorizon}
                onRefresh={handleRefresh}
            />

            <ScorecardTargetCard
                filteredScorecards={filteredScorecards}
                isLoading={scorecardsQuery.isLoading}
                strategyContext={strategyContext}
                selectedScorecardIdTop={selectedScorecardIdTop}
                onSelect={persistSelectedTop}
            />

            {/* Analysis Results */}
            {analysisQuery.isLoading || queueJobMutation.isPending ? (
                <DataLoadingSpinner text="Loading parameter ranking..." />
            ) : analysisQuery.error ? (
                <Card className="bg-zinc-900 p-6">
                    <div className="text-red-400">Error analyzing parameters: {analysisQuery.error.message}</div>
                </Card>
            ) : analysisResult ? (
                pendingAnalysis ? (
                    <PendingAnalysisCard
                        isRepairQueued={isExplicitRepairQueued}
                        isStartRepairPending={queueJobMutation.isPending}
                        pendingAnalysis={pendingAnalysis}
                        retryCount={retryCount}
                        onRetry={() => {
                            // Refetch the analysis
                            void analysisQuery.refetch();
                            incrementRetryCount();
                        }}
                        onStartRepair={handleRefresh}
                    />
                ) : readyAnalysis ? (
                    <>
                        {/* Summary Stats */}
                        <AnalysisSummaryStats readyAnalysis={readyAnalysis} />

                        {/* Unified Parameter Analysis Table */}
                        <ParameterAnalysisTableCard
                            archivedCount={archivedCount}
                            filteredTableData={filteredTableData}
                            optimizationMode={optimizationMode}
                            parameterColumnPreset={parameterColumnPreset}
                            pinnedCount={pinnedCount}
                            renderSubComponent={renderSubComponent}
                            setParameterColumnPreset={setParameterColumnPreset}
                            setShowArchived={setShowArchived}
                            setTrailingWindowFilter={setTrailingWindowFilter}
                            setViewMode={setViewMode}
                            showArchived={showArchived}
                            trailingWindowCounts={trailingWindowCounts}
                            trailingWindowFilter={trailingWindowFilter}
                            viewMode={viewMode}
                            visibleParameterColumns={visibleParameterColumns}
                            onScopedReset={handleScopedReset}
                        />

                        {/* Feature Pair Findings — built on top of parameter discovery evidence */}
                        <FeatureInteractionFindingsCard
                            experimentId={experimentId}
                            metric={optimizationMode}
                            selectedDuration={selectedDuration}
                            selectedHorizon={selectedHorizon}
                        />
                    </>
                ) : null
            ) : null}

            {/* Custom Buckets Modal */}
            <CustomBucketsModal
                bucketAlgorithm={bucketAlgorithm}
                bucketCount={bucketCount}
                bucketMode={bucketMode}
                customBucketsByIdentityKey={customBucketsByIdentityKey}
                customRanges={customRanges}
                executionStatus={executionStatus}
                experimentId={experimentId}
                getParameterDisplayLabel={getParameterDisplayLabel}
                hasInitialized={hasInitialized}
                identity={bucketModalIdentity}
                isOpen={bucketModalOpen}
                optimizationMode={optimizationMode}
                objectiveMode={objectiveMode}
                parameter={bucketModalParameter}
                realizedOutcomeSetId={realizedOutcomeSetId}
                selectedDuration={selectedDuration}
                selectedHorizon={selectedHorizon}
                setBucketAlgorithm={setBucketAlgorithm}
                setBucketCount={setBucketCount}
                setBucketMode={setBucketMode}
                setCustomBucketsByIdentityKey={setCustomBucketsByIdentityKey}
                setCustomRanges={setCustomRanges}
                onClose={closeBucketModal}
            />

            {/* Add to Scorecard Modal */}
            <AddRuleModal
                candidate={candidate}
                createNew={createNew}
                experimentId={experimentId}
                filteredScorecards={filteredScorecards}
                getParameterDisplayLabel={getParameterDisplayLabel}
                isOpen={addModalOpen}
                optimizationMode={optimizationMode}
                strategyContext={strategyContext}
                selectedDuration={selectedDuration}
                selectedHorizon={selectedHorizon}
                selectedScorecardId={selectedScorecardId}
                setCreateNew={setCreateNew}
                setSelectedScorecardId={setSelectedScorecardId}
                onClose={handleCloseModal}
                onRuleAdded={handleRuleAdded}
            />

            {/* Confirm bulk add dialog */}
            <ConfirmationDialog
                confirmText={bulkConfirm?.count ? `Add ${bulkConfirm.count}` : 'Confirm'}
                isOpen={!!bulkConfirm?.open}
                message={
                    bulkConfirm?.count
                        ? `Add ${bulkConfirm.count} values to the selected scorecard?`
                        : 'Add all values to the selected scorecard?'
                }
                title="Confirm Bulk Add"
                onCancel={() => setBulkConfirm(null)}
                onConfirm={() => bulkConfirm && quickAddAllForParameter(bulkConfirm.parameterPath, bulkConfirm.scorecardCandidate)}
            />
        </div>
    );
};

export default React.memo(ParameterDiscovery);
